export interface PropRow {
    name: string;
    type: string;
    defaultValue?: string;
    description: string;
}

export const recorderProps: PropRow[] = [
    {
        name: 'initialFacing',
        type: "'user' | 'environment'",
        defaultValue: "'user'",
        description: 'Camera facing mode to request first (front vs. rear).',
    },
    {
        name: 'recordRTCOptions',
        type: 'RecordRTCOptions',
        description:
            "Passed straight through to new RecordRTC(stream, options), merged over { type: 'video' } — mimeType, bitrate, timeSlice/ondataavailable, disableLogs, etc.",
    },
    {
        name: 'onRecordingComplete',
        type: '(blob: Blob, url: string) => void',
        description: "Called with the recording's blob and object URL once it stops.",
    },
    {
        name: 'onRecordingStateChanged',
        type: '(state: RecordingState) => void',
        description: "Mirrors RecordRTC's own onStateChanged callback.",
    },
    {
        name: 'onPhotoCapture',
        type: '(blob: Blob, url: string) => void',
        description: 'Called with a captured photo’s blob and object URL.',
    },
    {
        name: 'onPermissionChange',
        type: '(permission: PermissionState) => void',
        description: 'Called whenever camera permission/access state changes.',
    },
    {
        name: 'className',
        type: 'string',
        description: 'Extra class name applied to the root container.',
    },
    {
        name: 'style',
        type: 'CSSProperties',
        description: 'Extra inline styles merged onto the root container.',
    },
];

export const useCameraStreamReturns: PropRow[] = [
    { name: 'camera', type: 'MediaStream | null', description: 'The active camera stream, once granted.' },
    { name: 'permission', type: 'PermissionState', description: "{ status: boolean; error: unknown }" },
    { name: 'facing', type: "'user' | 'environment'", description: 'Current facing mode.' },
    { name: 'isLoading', type: 'boolean', description: 'True while a getUserMedia request is in flight.' },
    { name: 'canFlipCamera', type: 'boolean', description: 'True when more than one camera was enumerated.' },
    { name: 'toggleFacing', type: '() => Promise<void>', description: "Switch between 'user' and 'environment'." },
    { name: 'cycleCamera', type: '() => Promise<void>', description: 'Step through every enumerated camera device by id.' },
    { name: 'destroyCamera', type: '() => void', description: 'Stop all tracks and release the camera.' },
    { name: 'enableCamera', type: '() => Promise<void>', description: 'Re-request the camera after destroyCamera().' },
];

export const useVideoRecorderReturns: PropRow[] = [
    {
        name: 'recorder',
        type: 'RecordRTC | null',
        description: 'The raw RecordRTC instance while active/paused — save(), writeToDisk(), getDataURL(), setRecordingDuration(), getState(), and anything else not wrapped below.',
    },
    { name: 'isRecording', type: 'boolean', description: '' },
    { name: 'isRecorded', type: 'boolean', description: 'True once a recording has finished.' },
    { name: 'isPaused', type: 'boolean', description: '' },
    {
        name: 'recordingState',
        type: 'RecordingState',
        description: "RecordRTC's own state machine: 'inactive' | 'recording' | 'stopped' | 'paused' | 'destroyed'.",
    },
    { name: 'recordURL', type: 'string | null', description: 'Object URL for the finished recording.' },
    { name: 'recordMimeType', type: 'string | null', description: 'The actual mime type RecordRTC produced (varies by browser).' },
    { name: 'startRecording', type: '(overrideOptions?: RecordRTCOptions) => void', description: '' },
    { name: 'stopRecording', type: '() => void', description: '' },
    { name: 'pauseRecording', type: '() => void', description: '' },
    { name: 'resumeRecording', type: '() => void', description: '' },
    { name: 'reset', type: '() => void', description: '' },
    { name: 'retry', type: '() => void', description: 'Discard the finished recording and go back to the live preview.' },
    { name: 'play', type: '() => void', description: 'Play the finished recording in the video element.' },
];

export const usePhotoCaptureReturns: PropRow[] = [
    { name: 'photoURL', type: 'string | null', description: 'Object URL for the captured frame.' },
    { name: 'takePhoto', type: '() => void', description: 'Grab the current video frame as a photo.' },
];
